import styled from 'styled-components'
import {useState, useEffect, useCallback} from 'react'
import { ImageSwiper, PrimaryButton, Layout } from '../components'
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import Rating from '@material-ui/lab/Rating';
import { useSelector, useDispatch } from 'react-redux'
import { db, FirebaseTimestamp } from '../firebase/index'
import HTMLReactParser from 'html-react-parser'
import { push } from 'connected-react-router'
import { addProductToCart } from '../reducks/users/operations'
import { getUserId } from '../reducks/users/selectors'

// 商品説明内の改行表示を有効にする
const compileReturnToBr = (text) => {
    if (text === '') {
        return text
    } else {
        return HTMLReactParser(text.replace(/\r?\n/g, '<br/>'))
    }
}

// 日付のスタイル
const dateToString = (date) => {
    return date.getFullYear() + '-' + 
        ('00' + (date.getMonth()+1)).slice(-2) + '-' + 
        ('00' + date.getDate()).slice(-2) 
}

const ProductDetail = () => {
    
    const dispatch = useDispatch()
    const selector = useSelector(state=>state)
    const path = selector.router.location.pathname
    const productId = path.split('/product/dp/')[1]
    const userId = getUserId(selector)

    const [product, setProduct] = useState('')
    const [variations, setVariations] = useState([])
    const [reviews, setReviews] = useState([])
    const [tempReviews] = useState([])
    const [tempVariations] = useState([])
    const [isPurchased, setIsPurchased] = useState(false)

    // 割引後価格
    let discountedPrice = null
    if (product) {
        discountedPrice = Math.round( product.price * (1 - product.discount / 100) )
    }
    
    // 商品データを取得
    useEffect(() => {
        if(productId){
            db.collection('products').doc(productId).get()
            .then(doc=>{
                const data = doc.data()
                setProduct(data)
            })
        }
        return false
    }, [productId])

    // バリエーション情報を取得
    useEffect(() => {
        if(productId){
        db.collection('products').doc(productId).collection('variations')
          .orderBy("order", "desc")
          .get()
          .then(snapshots => {
                  snapshots.forEach(snapshot =>{
                      const data = snapshot.data()
                      tempVariations.push({
                          id: data.id,
                          variationName: data.variationName,
                          quantity: data.quantity
                      })
                  })
                  setVariations(tempVariations)
              })
        }
        return false
    }, [productId,tempVariations])

    // レビュー情報を取得
    useEffect(() => {
        if(productId){
            db.collection('products').doc(productId).collection('review')
              .orderBy("created_at", "desc")
              .get()
              .then(snapshots => {
                    snapshots.forEach(snapshot =>{
                        const data = snapshot.data()
                        tempReviews.push({
                            id: data.id,
                            rating: data.rating,
                            title: data.title,
                            text: data.text,
                            date: data.created_at
                        })
                    })
                    setReviews(tempReviews)
                })
        }
        return false
    }, [productId,tempReviews])

    // 商品を購入済みかどうか判定
    useEffect(() => {
        const orders = []
        let orderedProducts = []
        if(userId) {
          db.collection('users').doc(userId)
            .collection('orders').get()
            .then(snapshots => {
              snapshots.forEach(snapshot => {
                orders.push(snapshot.data())
              })
            })
            .then(() => {
              orders.forEach(order => {
                orderedProducts.push(order.products.filter(product => {
                  return product.id === productId
                }))
              })
              orderedProducts = [].concat(...orderedProducts)
              setIsPurchased(orderedProducts.length > 0)
            })
        }
        return false
    }, [userId, isPurchased, productId])


    const addProduct = useCallback((variationId,variationName) => {
        const timestamp = FirebaseTimestamp.now();
        dispatch(addProductToCart({
            added_at: timestamp,
            description: product.description,
            brand: product.brand,
            category: product.category,
            price: product.price,
            discount: product.discount,
            images: product.images,
            name: product.name,
            variationId,
            variationName,
            productId: product.id,
            quantity: 1
        }))
    }, [product, dispatch])

    return (
        <Layout>
            <div className='spacer-100' />
            <section>
                {product ? (
                    <ProductDetailWrapper>
                        <Flexbox>
                            <ProductImageWrapper>
                                <SwiperImageWrapper>
                                    <ImageSwiper images={product.images} />
                                </SwiperImageWrapper>
                            </ProductImageWrapper>
                            <ProductInfoWrapper>
                                <div>
                                    <Breadcrumb>
                                        <span onClick={()=>dispatch(push('/product?brand='+product.brand))}>{product.brand.toUpperCase()}</span>
                                        {product.category && " > "}
                                        <span onClick={()=>dispatch(push('/product?brand='+product.brand+'&category='+product.category))}>{product.category.toUpperCase()}</span>
                                    </Breadcrumb>
                                    <div className='spacer-25' />
                                    <ProductTitle>{product.name}</ProductTitle>
                                </div>

                                <div className='spacer-100' />

                                <PriceWrapper>
                                    <PriceTag>&yen;{discountedPrice.toLocaleString()}</PriceTag>
                                    {product.discount > 0 && (
                                        <DiscountTag>{product.discount + '%OFF'}</DiscountTag>
                                    )}
                                </PriceWrapper>

                                <div className='spacer-50' />

                                { variations.length > 0 && (
                                    variations.map(variation => (
                                        <VariationWrapper key={variation.id}>
                                            <p>{variation.variationName}</p>
                                            <PrimaryButton 
                                                disabled={variation.quantity === 0} 
                                                label={variation.quantity === 0 ? 'Out of Stock'  : 'Add to Cart'  }
                                                operation={()=>addProduct(variation.id,variation.variationName)} 
                                            />
                                        </VariationWrapper>
                                    ))
                                )}

                                <div className='spacer-50' />

                                <Description>{compileReturnToBr(product.description)}</Description>
                            </ProductInfoWrapper>
                        </Flexbox>
                        <div className='spacer-50' />

                        <div className='spacer-25' />
                        <Divider/>
                        <div className='spacer-25' />

                        <div>
                            <div className='spacer-50' />
                            <h2>商品レビュー</h2>
                            <div className='spacer-50' />

                            { reviews.length > 0 ? (
                                reviews.map(review => (
                                    <ReviewWrapper key={review.id}>
                                        <RatingWrapper style={{marginBottom: '10px'}}>
                                            <Rating 
                                                name="read-only" value={review.rating} readOnly
                                                style={{marginRight: '10px'}}     
                                            />
                                            <ReviewTitle>{review.title}</ReviewTitle>
                                        </RatingWrapper>
                                        <p style={{fontSize:'.9rem',color:'#ccc'}}>{dateToString(review.date.toDate())}</p>
                                        <ReviewText>{review.text}</ReviewText>
                                        <Divider/>
                                    </ReviewWrapper>
                                ))
                            ) : (
                                <p style={{textAlign:'center'}}>レビューがまだありません</p>
                            )}
                            {isPurchased && (
                            <>
                            <div className='spacer-50' />
                            <div style={{textAlign:'center'}}>
                                <PrimaryButton label='レビューを書く' operation={()=>dispatch(push('/product/reviewedit/' + product.id))} />
                            </div>
                            </>
                            )}
                        </div>

                        <div className='spacer-50'></div>
                        <div style={{textAlign:'center'}}>
                            <Button onClick={()=> dispatch(push('/product?brand=' + product.brand))}>一覧に戻る</Button>
                        </div>
                    </ProductDetailWrapper>
                ):(
                    <>
                    <div className='spacer-50' />
                    <p style={{textAlign:'center'}}>商品が見つかりませんでした</p>
                    <div className='spacer-25' />
                    <div style={{textAlign:'center'}}>
                        <Button onClick={()=> dispatch(push('/'))}>ホームに戻る</Button>
                    </div>
                    </>
                )}
            </section>
            <div className='spacer-100'></div>
        </Layout>
    )
}

export default ProductDetail

const ProductDetailWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 960px;
    margin: 0 auto;
    @media screen and (max-width:768px){
        width: 100%;
        padding: 0 20px;
    }
`
const Flexbox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    @media screen and (max-width:768px){
        flex-wrap: wrap;
        flex-direction: column;
    }
`
const ProductImageWrapper = styled.div`
    width: 50%;
    @media screen and (max-width:768px){
        width: 100%;
    }
`
const ProductInfoWrapper= styled.div`
    width: 50%;
    padding-left: 30px;
    @media screen and (max-width:768px){
        width: 100%;
        padding-left: 0;
    }
`
const SwiperImageWrapper = styled.div`
    width: 480px;
    margin: 0 auto;
    img {
        width: 100%;
        max-height: 500px;
        object-fit: contain;
    }
    @media screen and (max-width:768px){
        width: 100%;
    }
`
const Breadcrumb = styled.p`
    @media screen and (max-width:768px){
        margin-top: 50px;
    }
`
const ProductTitle = styled.h1`
    font-size: 2.5rem !important;
    line-height: 1.1;
    text-align: left;
    position: relative;
    &:before {
        position: absolute;
        bottom: -40px;
        left: 0;
        width: 55%;
        height: 10px;
        content: '';
        background: #fff;
    }
    &:after {
        position: absolute;
        bottom: -50px;
        right: 0;
        width: 55%;
        height: 1px;
        content: '';
        background: #fff;
    }
`
const ReviewWrapper = styled.div`
    width: 480px;
    margin: 0 auto;
    padding: 10px;
    @media screen and (max-width:768px){
        width: 100%;
    }
`
const RatingWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`
const ReviewTitle = styled.h4`
    font-size: 1.2rem !important;
    padding: 5px;
`
const ReviewText = styled.p`
    font-size: 1rem;
    letter-spacing: 0.05rem;
    line-height: 1.5rem;
    margin-top: 5px;
    margin-bottom: 15px;
`

const VariationWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
`
const PriceWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`
const PriceTag = styled.span`
    font-size: 1.5rem;
    margin-right: 10px;
`
const DiscountTag = styled.span`
    width: 100px;
    font-size: .8rem;
    padding: 5px 10px;
    color: #333;
    background-color: #FCEB04;
    border-radius: 4px;
    text-align: center;
`
const Description = styled.p`
    line-height: 28px;
`